import {
  surveyOutline,
  orderSectionQuestions,
  questionFamily,
} from "../../../../shared/survey-questions";
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextFormField,
} from "@/components/ui";
import type {
  MatterSurveyQuestionDefinition,
  MatterSurveyQuestionType,
} from "@/lib/data/requests/matters/matter-survey";
import { ListOrdered, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ReorderSurveyQuestionsModal } from "./ReorderSurveyQuestionsModal";
import { VirtualSurveyQuestionList } from "./VirtualSurveyQuestionList";
import { SurveyQuestionDisplayRule } from "./SurveyQuestionDisplayRule";

import {
  SurveySectionEditor,
  type SurveyDraftSection,
} from "./SurveySectionEditor";

export type SurveyDraftQuestion = MatterSurveyQuestionDefinition & {
  key: string;
  id?: string;
};

type QuestionFormValues = { questions: SurveyDraftQuestion[] };

export const surveyQuestionTypes: Array<{
  value: MatterSurveyQuestionType;
  label: string;
}> = [
  { value: "short_answer", label: "Short answer" },
  { value: "long_answer", label: "Long answer" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "checkboxes", label: "Checkboxes" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "datetime", label: "Date and time" },
];

export function newSurveyQuestion(): SurveyDraftQuestion {
  return {
    key: crypto.randomUUID(),
    prompt: "",
    type: "short_answer",
    required: true,
    options: [],
  };
}

export function isSurveyChoice(type: MatterSurveyQuestionType) {
  return type === "multiple_choice" || type === "checkboxes";
}

export function getSurveyQuestionErrors(
  questions: SurveyDraftQuestion[],
  sections: SurveyDraftSection[] = [],
) {
  const sectionIds = new Set(sections.map((section) => section.id));
  return questions.map((question) => ({
    prompt: !question.prompt.trim(),
    missingSection:
      sections.length > 0 &&
      !question.displayWhen &&
      !sectionIds.has(question.sectionId ?? ""),
    options: isSurveyChoice(question.type)
      ? question.options
          .filter((option) => !option.label.trim())
          .map((option) => option.id)
      : [],
    missingOptions:
      isSurveyChoice(question.type) && question.options.length === 0,
  }));
}

export function SurveyQuestionBuilder({
  questions,
  scrollElement,
  onChange,
  sections = [],
  onSectionsChange,
  disabled = false,
  showAddButton = true,
  showRemoveButton = true,
  validationAttempt = 0,
}: {
  questions: SurveyDraftQuestion[];
  scrollElement: HTMLDivElement | null;
  onChange: (questions: SurveyDraftQuestion[]) => void;
  sections?: SurveyDraftSection[];
  onSectionsChange?: (sections: SurveyDraftSection[]) => void;
  disabled?: boolean;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  validationAttempt?: number;
}) {
  const [revealRequest, setRevealRequest] = useState<{ key: string }>();
  const previousValidationAttempt = useRef(0);
  const showValidationErrors = validationAttempt > 0;
  const [reorderOpen, setReorderOpen] = useState(false);
  const reorderTriggerRef = useRef<HTMLButtonElement>(null);
  const form = useForm<QuestionFormValues>({
    values: { questions },
    resetOptions: { keepErrors: true },
  });
  const outline = surveyOutline(questions);
  const changeSections = (next: SurveyDraftSection[]) => {
    onSectionsChange?.(next);
    const assigned = questions.map((q) =>
      !sections.length && !q.displayWhen && !q.sectionId && next.length
        ? { ...q, sectionId: next[0].id }
        : q,
    );
    onChange(orderSectionQuestions(assigned, next));
  };
  const changeQuestions = (next: SurveyDraftQuestion[]) => {
    onChange(orderSectionQuestions(next, sections));
  };
  const labels = new Map(
    questions.map((question) => [
      question.key,
      outline.get(question.key).label,
    ]),
  );
  const errors = useMemo(
    () => getSurveyQuestionErrors(questions, sections),
    [questions, sections],
  );
  const { clearErrors, setError } = form;

  useEffect(() => {
    clearErrors("questions");
    if (!showValidationErrors) return;
    errors.forEach((error, index) => {
      if (error.prompt) {
        setError(`questions.${index}.prompt`, {
          message: "Question cannot be blank.",
        });
      }
      error.options.forEach((optionId) => {
        const optionIndex = questions[index].options.findIndex(
          (option) => option.id === optionId,
        );
        setError(`questions.${index}.options.${optionIndex}.label`, {
          message: "Option cannot be blank.",
        });
      });
    });
  }, [clearErrors, errors, questions, setError, showValidationErrors]);
  useEffect(() => {
    if (validationAttempt > previousValidationAttempt.current) {
      const index = errors.findIndex(
        (error) =>
          error.prompt ||
          error.missingSection ||
          error.missingOptions ||
          error.options.length > 0,
      );
      if (index >= 0) setRevealRequest({ key: questions[index].key });
    }
    previousValidationAttempt.current = validationAttempt;
  }, [errors, questions, validationAttempt]);

  const removeCondition = (question: SurveyDraftQuestion) => {
    const family = questionFamily(questions, question.key);
    const moving = new Set(family.map((q) => q.key));
    const remaining = questions.filter((q) => !moving.has(q.key));
    const updated = family.map((q) =>
      q.key === question.key
        ? {
            ...q,
            displayWhen: null,
            sectionId:
              questions.find((item) => item.key === outline.get(q.key).root)
                ?.sectionId ?? null,
          }
        : q,
    );
    changeQuestions([...remaining, ...updated]);
    setRevealRequest({ key: question.key });
  };

  const addConditionalQuestion = (parent: SurveyDraftQuestion) => {
    const family = questionFamily(questions, parent.key);
    const insertAt = questions.indexOf(family[family.length - 1]) + 1;
    const followUp: SurveyDraftQuestion = {
      ...newSurveyQuestion(),
      displayWhen: {
        questionId: parent.key,
        optionIds: [parent.options[0].id],
      },
    };
    changeQuestions([
      ...questions.slice(0, insertAt),
      followUp,
      ...questions.slice(insertAt),
    ]);
    setRevealRequest({ key: followUp.key });
  };

  const updateQuestion = (key: string, patch: Partial<SurveyDraftQuestion>) => {
    const familyKeys = new Set(
      patch.sectionId !== undefined
        ? questionFamily(questions, key).map((question) => question.key)
        : [],
    );
    changeQuestions(
      questions.map((question) =>
        question.key === key
          ? { ...question, ...patch }
          : familyKeys.has(question.key)
            ? { ...question, sectionId: null }
            : question,
      ),
    );
  };

  return (
    <Form {...form}>
      <div className="space-y-3">
        {onSectionsChange ? (
          <SurveySectionEditor
            sections={sections}
            usedSectionIds={
              new Set(
                questions
                  .filter((q) => !q.displayWhen)
                  .map((q) => q.sectionId)
                  .filter((id): id is string => Boolean(id)),
              )
            }
            disabled={disabled}
            showValidationErrors={showValidationErrors}
            onChange={changeSections}
          />
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <Label>Questions *</Label>
          {questions.length > 1 ? (
            <Button
              ref={reorderTriggerRef}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => {
                setReorderOpen(true);
              }}
            >
              <ListOrdered className="size-4" /> Reorder
            </Button>
          ) : null}
        </div>
        <VirtualSurveyQuestionList
          items={questions}
          getKey={(question) => question.key}
          scrollElement={scrollElement}
          estimateSize={280}
          rowClassName="pb-3"
          revealRequest={revealRequest}
          renderItem={(question, index) => {
            const questionErrors = showValidationErrors ? errors[index] : null;
            return (
              <div
                key={question.key}
                className="border-border space-y-3 rounded-xl border p-4"
                style={{
                  marginLeft: `${Math.min(outline.get(question.key).depth, 3) * 16}px`,
                }}
              >
                {!question.displayWhen && sections.length ? (
                  <div className="space-y-2">
                    <Label htmlFor={`question-${question.key}-section`}>
                      Section *
                    </Label>
                    <Select
                      value={question.sectionId ?? "unassigned"}
                      disabled={disabled}
                      onValueChange={(sectionId) => {
                        updateQuestion(question.key, {
                          sectionId:
                            sectionId === "unassigned" ? null : sectionId,
                        });
                        setRevealRequest({ key: question.key });
                      }}
                    >
                      <SelectTrigger
                        id={`question-${question.key}-section`}
                        aria-invalid={Boolean(questionErrors?.missingSection)}
                        aria-describedby={
                          questionErrors?.missingSection
                            ? `question-${question.key}-section-error`
                            : undefined
                        }
                      >
                        <SelectValue placeholder="Choose a section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {sections.map((section) => (
                          <SelectItem key={section.id} value={section.id}>
                            {section.title || "Untitled section"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {questionErrors?.missingSection ? (
                      <p
                        id={`question-${question.key}-section-error`}
                        role="alert"
                        className="text-destructive text-sm"
                      >
                        Choose a section for this question.
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {question.displayWhen ? (
                  <SurveyQuestionDisplayRule
                    question={question}
                    previousQuestions={questions.slice(0, index)}
                    labels={labels}
                    disabled={disabled}
                    onRemoveCondition={() => {
                      removeCondition(question);
                    }}
                    onOptionChange={(optionId, checked) => {
                      const condition = question.displayWhen;
                      if (!condition) return;
                      updateQuestion(question.key, {
                        displayWhen: {
                          questionId: condition.questionId,
                          optionIds: checked
                            ? [...condition.optionIds, optionId]
                            : condition.optionIds.filter(
                                (id) => id !== optionId,
                              ),
                        },
                      });
                    }}
                  />
                ) : null}
                <div className="flex items-start gap-2">
                  <TextFormField
                    register={form.register(`questions.${index}.prompt`)}
                    label={`Question ${outline.get(question.key).label} *`}
                    className="min-w-0 flex-1"
                    disabled={disabled}
                    handleChange={(value) => {
                      updateQuestion(question.key, { prompt: value });
                    }}
                  />
                  {showRemoveButton ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="mt-4"
                      aria-label={`Remove question ${index + 1}`}
                      disabled={disabled}
                      onClick={() => {
                        changeQuestions(
                          questions.filter(
                            (item) =>
                              !questionFamily(questions, question.key).includes(
                                item,
                              ),
                          ),
                        );
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={question.type}
                    disabled={disabled}
                    onValueChange={(value: MatterSurveyQuestionType) => {
                      updateQuestion(question.key, {
                        type: value,
                        options: isSurveyChoice(value)
                          ? question.options.length
                            ? question.options
                            : [
                                { id: crypto.randomUUID(), label: "" },
                                { id: crypto.randomUUID(), label: "" },
                              ]
                          : [],
                      });
                    }}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {surveyQuestionTypes.map((type) => (
                        <SelectItem
                          key={type.value}
                          value={type.value}
                          disabled={
                            !isSurveyChoice(type.value) &&
                            questions.some(
                              (q) => q.displayWhen?.questionId === question.key,
                            )
                          }
                        >
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormField
                    control={form.control}
                    name={`questions.${index}.required`}
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                            checked={field.value}
                            disabled={disabled}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              updateQuestion(question.key, {
                                required: checked === true,
                              });
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-foreground text-sm">
                          Required
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
                {isSurveyChoice(question.type) ? (
                  <div className="space-y-2 pl-3">
                    {question.options.map((option, optionIndex) => (
                      <div key={option.id} className="flex items-start gap-2">
                        <TextFormField
                          register={form.register(
                            `questions.${index}.options.${optionIndex}.label`,
                          )}
                          label={`Option ${optionIndex + 1} *`}
                          className="min-w-0 flex-1"
                          disabled={disabled}
                          handleChange={(value) => {
                            updateQuestion(question.key, {
                              options: question.options.map((item) =>
                                item.id === option.id
                                  ? { ...item, label: value }
                                  : item,
                              ),
                            });
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="mt-4"
                          aria-label={`Remove option ${optionIndex + 1}`}
                          disabled={
                            disabled ||
                            question.options.length === 1 ||
                            questions.some(
                              (q) =>
                                q.displayWhen?.questionId === question.key &&
                                q.displayWhen.optionIds.includes(option.id),
                            )
                          }
                          onClick={() => {
                            updateQuestion(question.key, {
                              options: question.options.filter(
                                (item) => item.id !== option.id,
                              ),
                            });
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    ))}
                    {questionErrors?.missingOptions ? (
                      <p role="alert" className="text-destructive text-sm">
                        Add at least one option.
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => {
                        updateQuestion(question.key, {
                          options: [
                            ...question.options,
                            { id: crypto.randomUUID(), label: "" },
                          ],
                        });
                      }}
                    >
                      <Plus className="size-4" /> Add option
                    </Button>
                  </div>
                ) : null}
                {isSurveyChoice(question.type) ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || question.options.length === 0}
                    onClick={() => {
                      addConditionalQuestion(question);
                    }}
                  >
                    <Plus className="size-4" /> Add conditional question
                  </Button>
                ) : null}
              </div>
            );
          }}
        />
        {questions.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Add a question to get started.
          </p>
        ) : null}
        {showAddButton ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => {
              const question = {
                ...newSurveyQuestion(),
                sectionId: sections.at(-1)?.id ?? null,
              };
              changeQuestions([...questions, question]);
              setRevealRequest({ key: question.key });
            }}
          >
            <Plus className="size-4" /> Add question
          </Button>
        ) : null}
        {showValidationErrors && questions.length === 0 ? (
          <p role="alert" className="text-destructive text-sm">
            Add at least one question.
          </p>
        ) : null}
      </div>
      {reorderOpen ? (
        <ReorderSurveyQuestionsModal
          questions={questions}
          sections={sections}
          onClose={() => {
            setReorderOpen(false);
          }}
          onSave={changeQuestions}
          onRestoreFocus={() => reorderTriggerRef.current?.focus()}
        />
      ) : null}
    </Form>
  );
}
